/*
 * ebook-kiler.js — Shared scroll/animation utilities for the Kiler landing page.
 *
 * Shared utilities for the Kiler landing page (DOMContentLoaded + section:load):
 *   1. Reveal-on-scroll — `data-ebook-reveal`
 *   2. Word-by-word typewriter — `data-ebook-typewriter` (TV media)
 *   3. Scroll-linked rotation — `window.EbookKiler.bindScrollRotation()`
 */

(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

  var mobileQuery = window.matchMedia
    ? window.matchMedia('(max-width: 749px)')
    : { matches: false, addEventListener: function () {}, removeEventListener: function () {} };

  function isMobile() {
    return mobileQuery.matches;
  }

  // ---- Reveal on scroll ------------------------------------------------

  function initReveal() {
    var targets = document.querySelectorAll('[data-ebook-reveal]');
    if (!targets.length) return;

    targets.forEach(function (el) {
      el.classList.add('ebook-reveal');
    });

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '-10% 0px -10% 0px', threshold: 0 }
    );

    targets.forEach(function (el) {
      observer.observe(el);
    });
  }

  // ---- Scroll-linked rotation -----------------------------------------

  function bindScrollRotation(config) {
    var trigger = typeof config.trigger === 'string'
      ? document.querySelector(config.trigger)
      : config.trigger;
    var target = typeof config.target === 'string'
      ? document.querySelector(config.target)
      : config.target;

    if (!trigger || !target) return function () {};

    var fromDeg = typeof config.fromDeg === 'number' ? config.fromDeg : 0;
    var toDeg = typeof config.toDeg === 'number' ? config.toDeg : 90;
    var disableMobile = trigger.hasAttribute('data-disable-mobile') || config.disableMobile === true;

    var ticking = false;
    var lastDeg = null;

    function disabled() {
      return prefersReducedMotion || (disableMobile && isMobile());
    }

    function applyRotation() {
      ticking = false;

      if (disabled()) {
        if (lastDeg !== fromDeg) {
          target.style.transform = 'rotate(' + fromDeg + 'deg)';
          lastDeg = fromDeg;
        }
        return;
      }

      var rect = trigger.getBoundingClientRect();
      var viewport = window.innerHeight || document.documentElement.clientHeight;
      // Progress from 0 (trigger top hits viewport bottom) to 1 (trigger bottom hits viewport top).
      var total = rect.height + viewport;
      var travelled = viewport - rect.top;
      var progress = total > 0 ? travelled / total : 0;
      if (progress < 0) progress = 0;
      if (progress > 1) progress = 1;

      var deg = fromDeg + (toDeg - fromDeg) * progress;
      if (lastDeg === null || Math.abs(deg - lastDeg) > 0.1) {
        target.style.transform = 'rotate(' + deg + 'deg)';
        lastDeg = deg;
      }
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(applyRotation);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    if (mobileQuery.addEventListener) {
      mobileQuery.addEventListener('change', onScroll);
    }
    applyRotation();

    return function unbind() {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (mobileQuery.removeEventListener) {
        mobileQuery.removeEventListener('change', onScroll);
      }
    };
  }

  // ---- Word-by-word typewriter (TV media, etc.) -----------------------

  /**
   * Split text nodes inside `root` into word spans, then reveal them
   * sequentially when `root` enters the viewport.
   *
   * @param {HTMLElement} root — element with data-ebook-typewriter
   */
  function initTypewriterRoot(root) {
    if (!root || root.getAttribute('data-typewriter-ready') === '1') return;

    var ms = parseInt(root.getAttribute('data-typewriter-ms') || '80', 10);
    if (isNaN(ms) || ms < 0) ms = 80;

    var paragraphs = root.querySelectorAll('.ebook-tv-media__paragraph');
    if (!paragraphs.length) return;

    paragraphs.forEach(function wrapWordsInParagraph(p) {
      var walker = document.createTreeWalker(p, NodeFilter.SHOW_TEXT, null);
      var textNodes = [];
      while (walker.nextNode()) {
        textNodes.push(walker.currentNode);
      }

      textNodes.forEach(function (textNode) {
        var text = textNode.textContent;
        if (!text || !text.trim()) return;

        var parts = text.split(/(\s+)/);
        var frag = document.createDocumentFragment();

        parts.forEach(function (part) {
          if (part === '') return;
          if (/^\s+$/.test(part)) {
            frag.appendChild(document.createTextNode(part));
          } else {
            var span = document.createElement('span');
            span.className = 'ebook-typewriter-word';
            span.textContent = part;
            frag.appendChild(span);
          }
        });

        textNode.parentNode.replaceChild(frag, textNode);
      });
    });

    var words = root.querySelectorAll('.ebook-typewriter-word');
    if (!words.length) return;

    root.setAttribute('data-typewriter-ready', '1');

    function revealAll() {
      words.forEach(function (w) {
        w.classList.add('ebook-typewriter-word--visible');
      });
    }

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      revealAll();
      return;
    }

    var i = 0;
    var timer = null;

    function tick() {
      if (i >= words.length) {
        if (timer) clearInterval(timer);
        timer = null;
        return;
      }
      words[i].classList.add('ebook-typewriter-word--visible');
      i += 1;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          if (timer) return;
          timer = setInterval(tick, ms);
          tick();
        });
      },
      { rootMargin: '80px 0px 80px 0px', threshold: 0 }
    );

    observer.observe(root);
  }

  function initTypewriter() {
    document.querySelectorAll('[data-ebook-typewriter]').forEach(initTypewriterRoot);
  }

  // ---- Scroll-timeline progress tracker ---------------------------------
  // For sections marked with [data-ebook-scrolly]: writes a CSS variable
  // `--scrolly-progress` (0 → 1) on the section root, based on how far the
  // user has scrolled through the section. CSS reads the variable to
  // interpolate opacity / transform on child scenes.
  //
  // Perf: scroll listener only attaches while the section is in viewport
  // (IntersectionObserver). On reduced-motion or screens ≤749px we bail
  // out — the CSS provides a static-stack fallback for those cases.

  function initScrollyRoot(root) {
    if (root.__scrollyInited) return;
    root.__scrollyInited = true;

    var mqMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    var mqMobile = window.matchMedia('(max-width: 749px)');
    if (mqMotion.matches || mqMobile.matches) return;

    var rafId = null;

    function update() {
      rafId = null;
      var rect = root.getBoundingClientRect();
      var travel = rect.height - window.innerHeight;
      if (travel <= 0) {
        root.style.setProperty('--scrolly-progress', '0');
        return;
      }
      var scrolled = -rect.top;
      var progress = scrolled / travel;
      if (progress < 0) progress = 0;
      else if (progress > 1) progress = 1;
      root.style.setProperty('--scrolly-progress', progress.toFixed(4));
    }

    function onScroll() {
      if (rafId === null) rafId = requestAnimationFrame(update);
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          window.addEventListener('scroll', onScroll, { passive: true });
          window.addEventListener('resize', onScroll, { passive: true });
          update();
        } else {
          window.removeEventListener('scroll', onScroll);
          window.removeEventListener('resize', onScroll);
        }
      });
    }, { rootMargin: '0px' });
    io.observe(root);
  }

  function initScrolly() {
    document.querySelectorAll('[data-ebook-scrolly]').forEach(initScrollyRoot);
  }

  // ---- Public API & boot ----------------------------------------------

  window.EbookKiler = window.EbookKiler || {};
  window.EbookKiler.bindScrollRotation = bindScrollRotation;
  window.EbookKiler.initReveal = initReveal;
  window.EbookKiler.initTypewriter = initTypewriter;
  window.EbookKiler.initScrolly = initScrolly;

  function boot() {
    initReveal();
    initTypewriter();
    initScrolly();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  /* Re-run when a section is injected (theme editor preview). */
  document.addEventListener('shopify:section:load', function () {
    initTypewriter();
    initScrolly();
  });
})();
